/**
 * One ranked list of everything Ori wants from you today.
 *
 * Owner, 2026-09-08, reading /today: *"all seem to be different and they are
 * confusing me ... What is my direction to follow? I want 1 guidance."*
 *
 * Five independent systems each grew their own card, and four of them spoke as
 * Ori with near-identical headings:
 *
 *   "Ori has a few things to ask"   admission proposals   structural decisions
 *   "Ori asks" · About "X"          fill-the-gaps         one missing field
 *   "Ori has today's plan ready"    attention curtain     prepared options
 *   "Ori noticed"                   goal review           celebrate / close
 *   "Ori asks: one more?"           release gate          one extra item
 *
 * So "break it down" and "today / this week / someday" read as the same kind
 * of question and are not: the first says the item has NO STRUCTURE, the
 * second says ONE FIELD is missing. And nothing ordered them against each
 * other, so the page offered five entry points and no direction.
 *
 * This module is pure and import-free so the ordering can be tested without a
 * browser: `npm run test:ori`. Adapters live in the component; only the shape
 * and the ranking live here.
 */

/** What a row is asking of you. The order of this union is the display order. */
export type OriAskKind =
  | 'offer'         // a prepared option, or the "one more?" offer — work on tap
  | 'needs_detail'  // admission is BLOCKED without this answer
  | 'break_down'    // the item has no concrete steps yet
  | 'decide'        // a trade-off, a batch to release, a pursue-now call
  | 'quick_answer'  // a field that would improve placement but blocks nothing
  | 'celebrate'     // finished work, and whether it closes

/**
 * Ranking, most-actionable first.
 *
 * The reasoning, so this is not re-shuffled by taste: work you could do right
 * now beats a decision; a decision that BLOCKS scheduling beats one that does
 * not; anything that changes what happens today beats a field that only tunes
 * a future ordering; and a celebration is the last thing you need to see,
 * because it is about work already done.
 */
export const ORI_RANK: Record<OriAskKind, number> = {
  offer: 0,
  needs_detail: 1,
  break_down: 2,
  decide: 3,
  quick_answer: 4,
  celebrate: 5,
}

/**
 * The words on the row's eyebrow. Every one STATES A FACT; none commands.
 *
 * This zone sits below "Start here", and the page's own spine says *"do the one
 * job under Start here … everything below those two is optional"* while this
 * zone's rule says *"Ignore them and nothing breaks."* The first labels here
 * were "Do now" and "Decide", which commanded action anyway — so the page had
 * two surfaces telling you what to do, one line apart from a rule saying it
 * did not. Owner, 2026-09-08: *"These are again mixed signals."*
 *
 * So: the eyebrow says what is TRUE of the item, and the buttons underneath
 * offer the verb. "No steps yet" + [Break it down] reads as a fact and an
 * offer; "Break it down" + [Break it down] read as an order, twice.
 */
export const ORI_KIND_LABEL: Record<OriAskKind, string> = {
  offer: 'If you have room',
  needs_detail: 'Needs a detail',
  break_down: 'No steps yet',
  decide: 'Needs a decision',
  quick_answer: 'Quick answer',
  celebrate: 'Finished',
}

/** How the item type reads on a row. Unknown types are title-cased, not hidden. */
const TYPE_WORD: Record<string, string> = {
  dream: 'Dream', goal: 'Goal', skill: 'Skill',
  habit: 'Habit', task: 'Task', action_item: 'Action item',
}

export function oriTypeWord(t: string | null | undefined): string | null {
  if (!t) return null
  const key = String(t).toLowerCase()
  return TYPE_WORD[key] ?? key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

export interface OriAsk {
  /** Stable across refetches: `${source}:${id}`. Used as the React key. */
  key: string
  kind: OriAskKind
  /** Which system raised it — for the probe, and for debugging on a live page. */
  source: 'curtain' | 'release' | 'admission' | 'gaps' | 'goal_review'
  /** The thing the ask is ABOUT. Both are shown; the type is what was missing. */
  itemType: string | null
  itemTitle: string | null
  /** One line, written to the person. Rendered verbatim. */
  line: string
  /** Secondary ordering inside a kind — the source's own rank, when it has one. */
  order?: number
}

/**
 * The whole list, in the order it should be read.
 *
 * Ties break on the source's own rank (the curtain ranks its options; admission
 * does not), then on the key, so the list never reshuffles between renders for
 * reasons the person cannot see.
 */
export function rankOriAsks<T extends OriAsk>(asks: T[]): T[] {
  return [...asks].sort((a, b) =>
    ORI_RANK[a.kind] - ORI_RANK[b.kind]
    || (a.order ?? 0) - (b.order ?? 0)
    || a.key.localeCompare(b.key))
}

/**
 * The one-line summary above the list.
 *
 * Names the top ask rather than counting, because a count ("4 to answer") is
 * what made the old inbox feel like a chore rather than a direction.
 */
export function oriSummary(asks: OriAsk[]): string | null {
  if (asks.length === 0) return null
  const [top] = rankOriAsks(asks)
  const rest = asks.length - 1
  const tail = rest === 0 ? '' : rest === 1 ? ' · 1 more' : ` · ${rest} more`
  return `${ORI_KIND_LABEL[top.kind]}${tail}`
}

/**
 * What the timeline should say when nothing is scheduled ahead.
 *
 * Owner, 2026-09-08: the page said "The rest of today is clear" while the
 * curtain held three live options — both true, and together a contradiction.
 * The timeline knew only about scheduled blocks; it now counts what is
 * actually on offer.
 */
export function restOfDayWords(liveOptions: number): string {
  if (liveOptions <= 0) return 'The rest of today is clear. That is a good way for a day to go.'
  if (liveOptions === 1) return 'Nothing more is scheduled. There is 1 option waiting below if you want it.'
  return `Nothing more is scheduled. There are ${liveOptions} options waiting below if you want them.`
}
